import React, { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Divider, SegmentedControl, Text } from '@/components/ui';
import { DataTable } from '@/components/data-table';
import { FilePreviewModal } from '@/components/file-preview';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import { FileImporterFile } from '../types';
import {
  getCsvFileDownloadEndpoint,
  getWhitelistedStudyFileDownloadEndpoint,
  getWhitelistedStudyFileVersionDownloadEndpoint,
} from '@/utils/downloadEndpoints';

import {
  IconList,
  IconRefresh,
  IconSearch,
  IconUpload,
} from '@tabler/icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { ROUTES } from '@/config/routes';

import FileImporterFileActions from './FileImporterFileActions';
import { FileImporterFilesTableProps } from './types';
import {
  CsvFileVersionsModal,
  FileVersionsModal,
} from '@/components/file-versions';
import { useWhitelistedStudyFileVersions } from '@/services/whitelistedStudyFilesHooks';
import type {
  WhitelistedStudyFile,
  WhitelistedStudyFileVersion,
} from '@/types/whitelistedStudyFiles';

const FileImporterFilesTable: React.FC<FileImporterFilesTableProps> = ({
  files,
  loading = false,
  onDeleteFile,
  onRefresh,
  customEmptyContent: customEmptyContentProp,
  pagination,
  paginationState,
  onPaginationChange,
  actions,
  onUploadClick,
  canUpload = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [versionsModalOpen, setVersionsModalOpen] = useState(false);
  const [selectedFileForVersions, setSelectedFileForVersions] =
    useState<FileImporterFile | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [fileToPreview, setFileToPreview] = useState<FileImporterFile | null>(
    null
  );

  const canManageFiles = usePermissionAccess([
    PERMISSIONS.STUDY_FILES_ACCESS,
    PERMISSIONS.FILES_ACCESS,
    PERMISSIONS.CSV_ACCESS,
  ]);

  const hasFilesAccess = usePermissionAccess([PERMISSIONS.FILES_ACCESS]);

  const handlePaginationChange = React.useCallback(
    (
      updater:
        | { pageIndex: number; pageSize: number }
        | ((prev: { pageIndex: number; pageSize: number }) => {
            pageIndex: number;
            pageSize: number;
          })
    ) => {
      if (!onPaginationChange) return;
      if (typeof updater === 'function') {
        const prev = paginationState || { pageIndex: 0, pageSize: 10 };
        onPaginationChange(updater(prev));
        return;
      }
      onPaginationChange(updater);
    },
    [onPaginationChange, paginationState]
  );

  const handleShowVersions = React.useCallback((file: FileImporterFile) => {
    setSelectedFileForVersions(file);
    setVersionsModalOpen(true);
  }, []);

  const handleVersionsModalOpenChange = React.useCallback((open: boolean) => {
    setVersionsModalOpen(open);
    if (!open) {
      setSelectedFileForVersions(null);
    }
  }, []);

  const handlePreview = useCallback(
    (file: FileImporterFile) => {
      if (canManageFiles) {
        setFileToPreview(file);
        setPreviewModalOpen(true);
      }
    },
    [canManageFiles]
  );

  const handlePreviewModalChange = useCallback((open: boolean) => {
    setPreviewModalOpen(open);
    if (!open) {
      setFileToPreview(null);
    }
  }, []);

  // Generate API endpoint for preview based on file type
  const previewApiEndpoint = fileToPreview
    ? fileToPreview.file_type === 'csv'
      ? getCsvFileDownloadEndpoint(fileToPreview)
      : getWhitelistedStudyFileDownloadEndpoint(fileToPreview)
    : undefined;

  const enableColumnVisibility = true;
  const pageSizeOptions = useMemo(() => [10, 25, 50, 100], []);

  const columns: ColumnDef<FileImporterFile>[] = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        meta: {
          cell: {
            variant: 'id',
            size: 'small',
          },
          minSize: 40,
          maxSize: 64,
        },
      },
      {
        accessorKey: 'filename',
        header: 'File Name',
        filterFn: 'includesString',
        meta: {
          cell: {
            variant: 'fileName',
            showIcon: true,
            truncate: true,
          },
          required: true,
          // size: '1fr',
          minSize: 200,
          size: '1fr',
        },
      },
      {
        accessorKey: 'extension',
        header: 'Extension',
        enableSorting: false,
        meta: {
          cell: {
            variant: 'fileFormat',
          },
          size: 'auto',
        },
      },
      {
        accessorKey: 'size',
        header: 'Size',
        meta: {
          cell: {
            variant: 'fileSize',
          },
          size: 'auto',
        },
      },
      {
        accessorKey: 'content_type',
        header: 'Type',
        filterFn: 'includesString',
        meta: {
          cell: {
            variant: 'mimeType',
          },
          size: 'auto',
          defaultVisible: false,
        },
      },
      {
        accessorKey: 'user',
        header: 'Uploaded by',
        enableSorting: false,
        meta: {
          cell: {
            variant: 'user',
            showEmail: true,
          },
          size: 'auto',
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Uploaded',
        meta: {
          cell: {
            variant: 'datetime',
            format: 'MMM D, YYYY h:mm A',
          },
          size: 'auto',
        },
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <FileImporterFileActions
            file={row.original}
            onDelete={onDeleteFile}
            onPreview={handlePreview}
            onShowVersions={handleShowVersions}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          width: 'auto',
          systemColumn: true,
        },
      },
    ],
    [onDeleteFile, handlePreview, handleShowVersions]
  );

  const rightSlot = useMemo(() => {
    // Determine current route for SegmentControl
    const currentPath = location.pathname;
    const isUploadPage = currentPath === ROUTES.fileImporter;
    const isFilesPage = currentPath === ROUTES.fileImporterFiles;
    const segmentValue = isFilesPage
      ? ROUTES.fileImporterFiles
      : ROUTES.fileImporter;

    const handleSegmentChange = (value: string) => {
      if (value !== currentPath) {
        navigate(value);
      }
    };

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          onClick={onRefresh}
          startIcon={<IconRefresh />}
          disabled={loading}
        >
          Refresh
        </Button>
        {canUpload && (
          <Button
            variant="classic"
            size="small"
            color="primary"
            startIcon={<IconUpload />}
            as={Link}
            to={ROUTES.fileImporter}
          >
            Upload Files
          </Button>
        )}

        {actions}

        {/* SegmentControl for page switching */}
        {hasFilesAccess && (isUploadPage || isFilesPage) && (
          <>
            <Divider orientation="vertical" size="small" />
            <SegmentedControl
              items={[
                {
                  value: ROUTES.fileImporter,
                  label: (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <IconUpload size={16} />
                      Upload
                    </span>
                  ),
                },
                {
                  value: ROUTES.fileImporterFiles,
                  label: (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <IconList size={16} />
                      Files
                    </span>
                  ),
                },
              ]}
              value={segmentValue}
              onValueChange={handleSegmentChange}
              size="medium"
              variant="surface"
            />
          </>
        )}
      </>
    );
  }, [
    canUpload,
    onRefresh,
    loading,
    actions,
    location.pathname,
    navigate,
    hasFilesAccess,
  ]);

  // Memoize pagination to avoid recreating on every render
  const paginationConfig = useMemo(
    () => ({
      pageSize: paginationState?.pageSize || 10,
      pageIndex: paginationState?.pageIndex || 0,
      pageCount: pagination?.total_pages || 0,
      totalCount: pagination?.total_count || 0,
      onPaginationChange: handlePaginationChange,
    }),
    [
      paginationState?.pageSize,
      paginationState?.pageIndex,
      pagination?.total_pages,
      pagination?.total_count,
      handlePaginationChange,
    ]
  );

  // Custom empty content for when no files exist
  const customEmptyContent = customEmptyContentProp
    ? customEmptyContentProp
    : onUploadClick ? (
    <div className="text-center py-8">
      <Text size="3" color="gray">
        No files uploaded yet
      </Text>
      <Button
        variant="soft"
        size="small"
        onClick={onUploadClick}
        className="mt-4"
      >
        Upload Files
      </Button>
    </div>
    ) : undefined;

  return (
    <>
      <DataTable
        data={files}
        columns={columns}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={true}
        manualPagination={true}
        enableColumnVisibility={enableColumnVisibility}
        enableRowSelection={false}
        loading={loading}
        searchPlaceholder="Search files..."
        pageSizeOptions={pageSizeOptions}
        toolbarLeftSlot={null}
        toolbarRightSlot={rightSlot}
        emptyMessage="No files found"
        emptyIcon={<IconSearch />}
        customEmptyContent={customEmptyContent}
        getRowId={(row: FileImporterFile) => row.id.toString()}
        pagination={paginationConfig}
      />

      {/* Versions Modal */}
      {selectedFileForVersions &&
        selectedFileForVersions.file_type === 'csv' && (
          <CsvFileVersionsModal
            file={selectedFileForVersions}
            open={versionsModalOpen}
            onOpenChange={handleVersionsModalOpenChange}
          />
        )}
      {selectedFileForVersions &&
        selectedFileForVersions.file_type === 'whitelisted_study' && (
          <FileVersionsModal<WhitelistedStudyFile, WhitelistedStudyFileVersion>
            file={selectedFileForVersions as WhitelistedStudyFile}
            open={versionsModalOpen}
            onOpenChange={handleVersionsModalOpenChange}
            useVersionsHook={useWhitelistedStudyFileVersions}
            getDownloadEndpoint={getWhitelistedStudyFileVersionDownloadEndpoint}
            modalTitle="Version History"
            modalSize="medium"
            showExtensionColumn={true}
          />
        )}

      {/* File Preview Modal */}
      <FilePreviewModal
        key={`preview-${fileToPreview?.id || 'none'}`}
        apiEndpoint={previewApiEndpoint}
        filename={fileToPreview?.filename || ''}
        mimeType={fileToPreview?.content_type}
        fileSize={fileToPreview?.size}
        open={previewModalOpen && !!fileToPreview}
        onOpenChange={handlePreviewModalChange}
        title={fileToPreview ? `Preview: ${fileToPreview.filename}` : 'Preview'}
      />
    </>
  );
};

export default FileImporterFilesTable;
